import { type FC, useEffect, useRef } from "react";
import { Plus, X } from "lucide-react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useAppContext } from "@/shared/hooks/useAppContext";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type {
  FamilyTriagePersonRow,
  HasLivingParentsAnswer,
  HasSiblingsAnswer,
  PersonFlowStatus,
} from "@/shared/types/probateTypes";

import { createEmptyRow } from "./IssueRow";
import { getFirstName, getLastName, setFirstName, setLastName, NOT_SURE_CAPTION } from "./nameUtils";
import { useJsonListAnswer } from "./useJsonListAnswer";

const PARENTS_KEY = "family_triage_has_living_parents";
const PARENTS_LIST_KEY = "family_triage_parents_list";
const SIBLINGS_KEY = "family_triage_has_siblings";
const SIBLINGS_LIST_KEY = "family_triage_siblings_list";

function createEmptyParent(): FamilyTriagePersonRow {
  return { ...createEmptyRow(), isMinor: "no" };
}

export const ExtendedFamilyScreen: FC<FlowCustomComponentProps> = ({ answers, setAnswer, errors }) => {
  const { person } = useAppContext();
  const name = person?.first_name ?? "the decedent";
  const hasErrors = !!errors && Object.keys(errors).length > 0;

  const marriedAtDeath = answers["family_triage_married_at_death"] as string;
  const spouseStatus = answers["family_triage_spouse_status"] as string;
  const hasLivingSpouse = marriedAtDeath === "yes" && spouseStatus === "living";

  // Conditional parents mode (living spouse, no children)
  // vs Extended family mode (no living spouse, no living descendants)
  const isConditionalParentsMode = hasLivingSpouse;

  const hasLivingParents = (answers[PARENTS_KEY] as HasLivingParentsAnswer) ?? "";
  const hasSiblings = (answers[SIBLINGS_KEY] as HasSiblingsAnswer) ?? "";

  const [parentsList, syncParents] = useJsonListAnswer<FamilyTriagePersonRow>(
    answers,
    PARENTS_LIST_KEY,
    setAnswer,
  );

  const [siblingsList, syncSiblings] = useJsonListAnswer<FamilyTriagePersonRow>(
    answers,
    SIBLINGS_LIST_KEY,
    setAnswer,
  );

  const handleParentsSelect = (value: string) => {
    const val = value as HasLivingParentsAnswer;
    setAnswer(PARENTS_KEY, val);
    if (val === "yes" && parentsList.length === 0) {
      syncParents([createEmptyParent()]);
    } else if (val !== "yes") {
      syncParents([]);
    }
  };

  const handleSiblingsSelect = (value: string) => {
    const val = value as HasSiblingsAnswer;
    setAnswer(SIBLINGS_KEY, val);
    if (val === "yes" && siblingsList.length === 0) {
      syncSiblings([createEmptyRow()]);
    } else if (val !== "yes") {
      syncSiblings([]);
    }
  };

  const handleParentChange = (index: number, updated: FamilyTriagePersonRow) => {
    const next = [...parentsList];
    next[index] = updated;
    syncParents(next);
  };

  const handleSiblingChange = (index: number, updated: FamilyTriagePersonRow) => {
    const next = [...siblingsList];
    next[index] = updated;
    syncSiblings(next);
  };

  const parentsLengthRef = useRef(parentsList.length);
  parentsLengthRef.current = parentsList.length;
  const syncParentsRef = useRef(syncParents);
  syncParentsRef.current = syncParents;
  const seededParentsRef = useRef(false);

  useEffect(() => {
    if (isConditionalParentsMode && parentsLengthRef.current === 0 && !seededParentsRef.current) {
      seededParentsRef.current = true;
      syncParentsRef.current([createEmptyParent()]);
    }
  }, [isConditionalParentsMode]);

  // No living parent resolved -> show siblings
  const noLivingParent =
    hasLivingParents === "no_living_parents" ||
    (hasLivingParents === "yes" && parentsList.length > 0 && parentsList.every((p) => p.status === "passed_away"));
  const showSiblings = !isConditionalParentsMode && noLivingParent;

  if (isConditionalParentsMode) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <p className="font-sans text-xl font-semibold leading-[30px]">
            {name}'s parents
          </p>
          <p className="text-sm text-secondary tracking-[-0.28px]">
            With a spouse and no surviving children, court rules require {name}'s
            parents on the family map. They will receive formal notice by mail,
            but nothing else is needed from them.
          </p>
        </div>

        {parentsList.map((parent, i) => (
          <div key={parent.id} className="flex flex-col gap-3 rounded-lg border border-primary p-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>First name</Label>
                <Input
                  placeholder="First name"
                  value={getFirstName(parent.name)}
                  onChange={(e) => handleParentChange(i, { ...parent, name: setFirstName(parent.name, e.target.value) })}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Last name</Label>
                <Input
                  placeholder="Last name"
                  value={getLastName(parent.name)}
                  onChange={(e) => handleParentChange(i, { ...parent, name: setLastName(parent.name, e.target.value) })}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Status</Label>
              <Select value={parent.status} onValueChange={(v) => handleParentChange(i, { ...parent, status: v as PersonFlowStatus })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="living">Living</SelectItem>
                  <SelectItem value="passed_away">Passed away</SelectItem>
                  <SelectItem value="not_sure">Not sure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {parent.status === "not_sure" && (
              <p className="text-xs text-secondary">{NOT_SURE_CAPTION}</p>
            )}
          </div>
        ))}

        <button
          type="button"
          onClick={() => syncParents([...parentsList, createEmptyParent()])}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary py-3 text-sm font-medium text-primary hover:bg-subtle"
        >
          <Plus className="size-4" />
          Add another
        </button>
      </div>
    );
  }

  // Extended family mode (no living spouse, no living descendants)
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          {name}'s closest family
        </p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          With no spouse or surviving children, the court looks to {name}'s
          parents next, then brothers and sisters.
        </p>
      </div>

      {/* Parents */}
      <div className="flex flex-col gap-4">
        <div className="border-t border-primary" />

        <div className="flex flex-col gap-1">
          <p className="font-sans text-xl font-semibold leading-[30px]">
            Is either of {name}'s parents living?
          </p>
          <p className="text-sm text-secondary tracking-[-0.28px]">
            With no spouse or surviving children, the court looks to {name}'s
            parents next to determine who must receive court notice or sign-off.
          </p>
        </div>

        <RadioGroup name="has_living_parents" value={hasLivingParents} onValueChange={handleParentsSelect} className="flex flex-col gap-3">
          <OptionCard label="Yes" selected={hasLivingParents === "yes"} onClick={() => handleParentsSelect("yes")} variant="radio" value="yes" />
          <OptionCard label="No living parents" selected={hasLivingParents === "no_living_parents"} onClick={() => handleParentsSelect("no_living_parents")} variant="radio" value="no_living_parents" />
        </RadioGroup>
        <FieldError message={errors?.has_living_parents} />

        {hasLivingParents === "yes" && (
          <div className="flex flex-col gap-4">
            <div className="border-t border-primary" />

            <div className="flex flex-col gap-1">
              <p className="font-sans text-xl font-semibold leading-[30px]">
                Add {name}'s parents
              </p>
              <p className="text-sm text-secondary tracking-[-0.28px]">
                The court asks for {name}'s parents on its family map. They
                typically receive formal notice by mail, but nothing else is
                needed from them.
              </p>
            </div>

            {parentsList.map((parent, i) => (
              <div key={parent.id} className="flex flex-col gap-3 rounded-lg border border-primary p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label>First name</Label>
                    <Input
                      placeholder="First name"
                      value={getFirstName(parent.name)}
                      onChange={(e) => handleParentChange(i, { ...parent, name: setFirstName(parent.name, e.target.value) })}
                      className={hasErrors && !getFirstName(parent.name).trim() ? "border-destructive" : ""}
                    />
                    {hasErrors && !getFirstName(parent.name).trim() && <FieldError message="First name is required" />}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>Last name</Label>
                    <Input
                      placeholder="Last name"
                      value={getLastName(parent.name)}
                      onChange={(e) => handleParentChange(i, { ...parent, name: setLastName(parent.name, e.target.value) })}
                      className={hasErrors && !getLastName(parent.name).trim() ? "border-destructive" : ""}
                    />
                    {hasErrors && !getLastName(parent.name).trim() && <FieldError message="Last name is required" />}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Status</Label>
                  <Select value={parent.status} onValueChange={(v) => handleParentChange(i, { ...parent, status: v as PersonFlowStatus })}>
                    <SelectTrigger className={hasErrors && !parent.status ? "border-destructive" : ""}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="living">Living</SelectItem>
                      <SelectItem value="passed_away">Passed away</SelectItem>
                      <SelectItem value="not_sure">Not sure</SelectItem>
                    </SelectContent>
                  </Select>
                  {hasErrors && !parent.status && <FieldError message="Status is required" />}
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => syncParents([...parentsList, createEmptyParent()])}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary py-3 text-sm font-medium text-primary hover:bg-subtle"
            >
              <Plus className="size-4" />
              Add another
            </button>
          </div>
        )}
      </div>

      {/* Siblings -- mounts when no living parent */}
      {showSiblings && (
        <div className="flex flex-col gap-4">
          <div className="border-t border-primary" />

          <div className="flex flex-col gap-1">
            <p className="font-sans text-xl font-semibold leading-[30px]">
              Did {name} have any siblings?
            </p>
            <p className="text-sm text-secondary tracking-[-0.28px]">
              Include half-siblings. The court treats them the same as full siblings.
            </p>
          </div>

          <RadioGroup name="has_siblings" value={hasSiblings} onValueChange={handleSiblingsSelect} className="flex flex-col gap-3">
            <OptionCard label="Yes" selected={hasSiblings === "yes"} onClick={() => handleSiblingsSelect("yes")} variant="radio" value="yes" />
            <OptionCard label="No siblings" selected={hasSiblings === "no_siblings"} onClick={() => handleSiblingsSelect("no_siblings")} variant="radio" value="no_siblings" />
          </RadioGroup>
          <FieldError message={errors?.has_siblings} />

          {hasSiblings === "yes" && (
            <div className="flex flex-col gap-4">
              <div className="border-t border-primary" />

              <p className="font-sans text-xl font-semibold leading-[30px]">
                Add {name}'s siblings
              </p>

              {siblingsList.map((sibling, i) => {
                const sibFirstErr = hasErrors && !getFirstName(sibling.name).trim() ? "First name is required" : undefined;
                const sibLastErr = hasErrors && !getLastName(sibling.name).trim() ? "Last name is required" : undefined;
                const sibStatusErr = hasErrors && !sibling.status ? "Status is required" : undefined;
                const canRemove = siblingsList.length > 1;
                const sibFirstName = getFirstName(sibling.name) || "this person";

                return (
                  <div key={sibling.id} className="relative rounded-lg border border-primary p-4">
                    {canRemove && (
                      <button
                        type="button"
                        onClick={() => syncSiblings(siblingsList.filter((_, j) => j !== i))}
                        className="absolute right-3 top-3 rounded-md p-1 text-tertiary hover:bg-subtle hover:text-primary"
                        aria-label="Remove"
                      >
                        <X className="size-4" />
                      </button>
                    )}
                    <div className={`flex flex-col gap-3 ${canRemove ? "pr-6" : ""}`}>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col gap-1.5">
                          <Label>First name</Label>
                          <Input
                            placeholder="First name"
                            value={getFirstName(sibling.name)}
                            onChange={(e) => handleSiblingChange(i, { ...sibling, name: setFirstName(sibling.name, e.target.value) })}
                            className={sibFirstErr ? "border-destructive" : ""}
                          />
                          <FieldError message={sibFirstErr} />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <Label>Last name</Label>
                          <Input
                            placeholder="Last name"
                            value={getLastName(sibling.name)}
                            onChange={(e) => handleSiblingChange(i, { ...sibling, name: setLastName(sibling.name, e.target.value) })}
                            className={sibLastErr ? "border-destructive" : ""}
                          />
                          <FieldError message={sibLastErr} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col gap-1.5">
                          <Label>Are they under 18 years old?</Label>
                          <Select
                            value={sibling.isMinor}
                            onValueChange={(v) => handleSiblingChange(i, { ...sibling, isMinor: v as "yes" | "no" })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="yes">Yes</SelectItem>
                              <SelectItem value="no">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <Label>Status</Label>
                          <Select value={sibling.status} onValueChange={(v) => {
                            const status = v as PersonFlowStatus;
                            if (status !== "passed_away") {
                              handleSiblingChange(i, { ...sibling, status, deathYear: "", hasChildren: "", children: [] });
                            } else {
                              handleSiblingChange(i, { ...sibling, status });
                            }
                          }}>
                            <SelectTrigger className={sibStatusErr ? "border-destructive" : ""}>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="living">Living</SelectItem>
                              <SelectItem value="passed_away">Passed away</SelectItem>
                              <SelectItem value="not_sure">Not sure</SelectItem>
                            </SelectContent>
                          </Select>
                          <FieldError message={sibStatusErr} />
                        </div>
                      </div>

                      {sibling.status === "passed_away" && (
                        <>
                          <div className="flex flex-col gap-1.5">
                            <Label>Did {sibFirstName} have children?</Label>
                            <Select
                              value={sibling.hasChildren}
                              onValueChange={(v) => {
                                const val = v as "yes" | "no" | "not_sure";
                                if (val === "yes" && sibling.children.length === 0) {
                                  handleSiblingChange(i, { ...sibling, hasChildren: val, children: [createEmptyRow()] });
                                } else if (val !== "yes") {
                                  handleSiblingChange(i, { ...sibling, hasChildren: val, children: [] });
                                } else {
                                  handleSiblingChange(i, { ...sibling, hasChildren: val });
                                }
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="yes">Yes</SelectItem>
                                <SelectItem value="no">No</SelectItem>
                                <SelectItem value="not_sure">Not sure</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-secondary">
                              If {sibFirstName} did, their children take that place on the court's list.
                            </p>
                          </div>

                          {sibling.hasChildren === "yes" && (
                            <div className="flex flex-col gap-4">
                              <p className="font-sans text-lg font-semibold">
                                Add {sibFirstName}'s children
                              </p>

                              {sibling.children.map((nephew, ni) => {
                                const canRemoveNephew = sibling.children.length > 1;
                                const nFirstErr = hasErrors && !getFirstName(nephew.name).trim() ? "First name is required" : undefined;
                                const nLastErr = hasErrors && !getLastName(nephew.name).trim() ? "Last name is required" : undefined;
                                const nStatusErr = hasErrors && !nephew.status ? "Status is required" : undefined;

                                return (
                                  <div key={nephew.id} className="relative rounded-lg border border-primary bg-subtle p-4">
                                    {canRemoveNephew && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          handleSiblingChange(i, {
                                            ...sibling,
                                            children: sibling.children.filter((_, j) => j !== ni),
                                          });
                                        }}
                                        className="absolute right-3 top-3 rounded-md p-1 text-tertiary hover:bg-subtle hover:text-primary"
                                        aria-label="Remove"
                                      >
                                        <X className="size-4" />
                                      </button>
                                    )}
                                    <div className={`flex flex-col gap-3 ${canRemoveNephew ? "pr-6" : ""}`}>
                                      <div className="grid grid-cols-2 gap-3">
                                        <div className="flex flex-col gap-1.5">
                                          <Label>First name</Label>
                                          <Input
                                            placeholder="First name"
                                            value={getFirstName(nephew.name)}
                                            onChange={(e) => {
                                              const next = [...sibling.children];
                                              next[ni] = { ...nephew, name: setFirstName(nephew.name, e.target.value) };
                                              handleSiblingChange(i, { ...sibling, children: next });
                                            }}
                                            className={nFirstErr ? "border-destructive" : ""}
                                          />
                                          <FieldError message={nFirstErr} />
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                          <Label>Last name</Label>
                                          <Input
                                            placeholder="Last name"
                                            value={getLastName(nephew.name)}
                                            onChange={(e) => {
                                              const next = [...sibling.children];
                                              next[ni] = { ...nephew, name: setLastName(nephew.name, e.target.value) };
                                              handleSiblingChange(i, { ...sibling, children: next });
                                            }}
                                            className={nLastErr ? "border-destructive" : ""}
                                          />
                                          <FieldError message={nLastErr} />
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-2 gap-3">
                                        <div className="flex flex-col gap-1.5">
                                          <Label>Are they under 18 years old?</Label>
                                          <Select
                                            value={nephew.isMinor}
                                            onValueChange={(v) => {
                                              const next = [...sibling.children];
                                              next[ni] = { ...nephew, isMinor: v as "yes" | "no" };
                                              handleSiblingChange(i, { ...sibling, children: next });
                                            }}
                                          >
                                            <SelectTrigger>
                                              <SelectValue placeholder="Select" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="yes">Yes</SelectItem>
                                              <SelectItem value="no">No</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                          <Label>Status</Label>
                                          <Select
                                            value={nephew.status}
                                            onValueChange={(v) => {
                                              const next = [...sibling.children];
                                              next[ni] = { ...nephew, status: v as PersonFlowStatus };
                                              handleSiblingChange(i, { ...sibling, children: next });
                                            }}
                                          >
                                            <SelectTrigger className={nStatusErr ? "border-destructive" : ""}>
                                              <SelectValue placeholder="Select" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="living">Living</SelectItem>
                                              <SelectItem value="passed_away">Passed away</SelectItem>
                                              <SelectItem value="not_sure">Not sure</SelectItem>
                                            </SelectContent>
                                          </Select>
                                          <FieldError message={nStatusErr} />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}

                              <button
                                type="button"
                                onClick={() =>
                                  handleSiblingChange(i, {
                                    ...sibling,
                                    children: [...sibling.children, createEmptyRow()],
                                  })
                                }
                                className="flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
                              >
                                <Plus className="size-3.5" />
                                Add another child
                              </button>
                            </div>
                          )}
                        </>
                      )}

                      {sibling.status === "not_sure" && (
                        <p className="text-xs text-secondary">{NOT_SURE_CAPTION}</p>
                      )}
                    </div>
                  </div>
                );
              })}

              <button
                type="button"
                onClick={() => syncSiblings([...siblingsList, createEmptyRow()])}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary py-3 text-sm font-medium text-primary hover:bg-subtle"
              >
                <Plus className="size-4" />
                Add another
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
